import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchCatBrands, type PrevBrandItem } from '@/api/catBrand';
import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import PrevBrandPicker, {
  formatPrevBrandSelection,
  isPrevBrandSelectionValid,
} from '@/components/survey/PrevBrandPicker';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearOtherPrevBrandAnswerAnswers } from '@/lib/otherPrevBrandAnswerStorage';
import {
  readOtherPrevBrandAnswers,
  writeOtherPrevBrandAnswers,
  clearOtherPrevBrandAnswers,
} from '@/lib/otherPrevBrandStorage';
import {
  OTHER_PRODUCT_CATEGORIES,
  isOtherProductCategory,
  readOtherProductTypeAnswers,
  writeOtherProductTypeAnswers,
  type OtherProductCategory,
} from '@/lib/otherProductTypeStorage';
import { PREV_BRAND_OTHERS, buildPrevBrandAnswers } from '@/lib/prevBrandStorage';
import { surveyBeforeOtherProductRedirect, surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

const OPTION_LABEL_KEY = {
  RMC: 'rmc',
  VAPE: 'vape',
  RRP: 'rrp',
} as const;

export default function PrevBrandOther() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [category, setCategory] = useState<OtherProductCategory | ''>(
    () => readOtherProductTypeAnswers().category
  );
  const [brands, setBrands] = useState<PrevBrandItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState(() => readOtherPrevBrandAnswers()?.brand ?? '');
  const [selectedVariant, setSelectedVariant] = useState(
    () => readOtherPrevBrandAnswers()?.variant ?? ''
  );

  const hasCategory = isOtherProductCategory(category);

  useEffect(() => {
    writeOtherProductTypeAnswers({ category });
  }, [category]);

  useEffect(() => {
    writeOtherPrevBrandAnswers({ brand: selectedBrand, variant: selectedVariant });
  }, [selectedBrand, selectedVariant]);

  useEffect(() => {
    let cancelled = false;
    if (!isOtherProductCategory(category)) {
      setBrands([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setWarning(null);
    void (async () => {
      try {
        const list = await fetchCatBrands(category);
        if (cancelled) return;
        setBrands(list);
      } catch {
        if (!cancelled) setWarning(t.prevBrand.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category, t.prevBrand.loadError]);

  useEffect(() => {
    if (loading || !hasCategory) return;
    if (!selectedBrand || selectedBrand === PREV_BRAND_OTHERS) return;
    const stillValid = brands.some(
      (item) => item.brand === selectedBrand && item.variants.includes(selectedVariant)
    );
    if (!stillValid) {
      setSelectedBrand('');
      setSelectedVariant('');
      setExpandedBrand(null);
    }
  }, [loading, hasCategory, brands, selectedBrand, selectedVariant]);

  useEffect(() => {
    if (selectedBrand && selectedBrand !== PREV_BRAND_OTHERS && !expandedBrand) {
      setExpandedBrand(selectedBrand);
    }
  }, [selectedBrand, expandedBrand]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  const beforeOtherRedirect =
    questionnaireSet === 'A' || questionnaireSet === 'B'
      ? surveyBeforeOtherProductRedirect(questionnaireSet)
      : '/survey';
  if (beforeOtherRedirect) return <Navigate to={beforeOtherRedirect} replace />;

  const handleCategoryChange = (next: OtherProductCategory | '') => {
    if (next !== category) {
      clearOtherPrevBrandAnswers();
      clearOtherPrevBrandAnswerAnswers();
      setSelectedBrand('');
      setSelectedVariant('');
      setExpandedBrand(null);
    }
    setCategory(isOtherProductCategory(next) ? next : '');
    setWarning(null);
  };

  const handleSelectVariant = (brand: string, variant: string) => {
    setSelectedBrand(brand);
    setSelectedVariant(variant);
    setWarning(null);
  };

  const handleOtherSpecifyChange = (value: string) => {
    const normalized = buildPrevBrandAnswers(PREV_BRAND_OTHERS, value);
    setSelectedBrand(normalized.brand);
    setSelectedVariant(normalized.variant);
    setExpandedBrand(null);
    setWarning(null);
  };

  const handleContinue = () => {
    if (!isOtherProductCategory(category)) {
      setWarning(t.otherProductType.requiredWarning);
      return;
    }
    if (selectedBrand === PREV_BRAND_OTHERS && !selectedVariant.trim()) {
      setWarning(t.prevBrand.otherSpecifyWarning);
      return;
    }
    if (!isPrevBrandSelectionValid(selectedBrand, selectedVariant)) {
      setWarning(t.prevBrand.requiredWarning);
      return;
    }
    writeOtherProductTypeAnswers({ category });
    writeOtherPrevBrandAnswers(buildPrevBrandAnswers(selectedBrand, selectedVariant));
    navigate('/survey/prevbrandanswer-other');
  };

  const hasSelection = isPrevBrandSelectionValid(selectedBrand, selectedVariant);

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto mt-[7vh] flex min-h-0 w-full max-w-full flex-1 flex-col">
          <SurveyContentPanelInset className="shrink-0">
            <SurveyFormPanel className="!mb-4 flex flex-col gap-1.5">
              <p id="other-product-type-question" className={surveyQuestionTextClass}>
                {t.otherProductType.question}
              </p>
              <select
                id="other-product-type"
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value as OtherProductCategory | '')}
                className={inputClass}
                aria-labelledby="other-product-type-question"
              >
                <option value="" disabled>
                  {t.otherProductType.selectProduct}
                </option>
                {OTHER_PRODUCT_CATEGORIES.map((option) => (
                  <option key={option} value={option}>
                    {t.otherProductType[OPTION_LABEL_KEY[option]]}
                  </option>
                ))}
              </select>
            </SurveyFormPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            {hasCategory ? (
              <PrevBrandPicker
                brands={brands}
                loading={loading}
                expandedBrand={expandedBrand}
                selectedBrand={selectedBrand}
                selectedVariant={selectedVariant}
                onExpandedBrandChange={setExpandedBrand}
                onSelectVariant={handleSelectVariant}
                onOtherSpecifyChange={handleOtherSpecifyChange}
              />
            ) : null}
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {hasSelection ? (
                <p className="m-0 text-center text-sm font-bold text-white/90">
                  {formatPrevBrandSelection(selectedBrand, selectedVariant, t.prevBrand.otherSpecify)}
                </p>
              ) : null}
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton disabled={loading} onClick={handleContinue}>
                {t.prevBrand.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
